/**
 * Big 12 Football Tiebreaker implementation (self-contained)
 *
 * Expected inputs:
 * - tiedTeams: Array of team objects for the tie. Each item should be at least { team: "TEAM_ABBR" }.
 * - confTeams: Conference standings array (all conference teams), each item like:
 *      { team: "TEAM_ABBR", conferenceWins: number, conferenceGames: number, totalWins: number }
 *   conferenceGames is optional; if missing a default (9) will be used in some calculations.
 * - games: Array of game objects. Each game should contain at minimum:
 *      { homeTeam: "TEAM", awayTeam: "TEAM", winner: "TEAM" | null, conferenceGame: boolean, exempt: boolean (optional), season: number (optional) }
 *   `exempt` marks games not counted toward the 12-game max (per NCAA); optional.
 * - options (optional): { ssarankings: { [team]: number }, rng: () => number }.
 *    ssarankings supplies SportSource Analytics rating score (higher = better).
 *    rng is a function returning [0,1) (for deterministic coin-toss in tests).
 *
 * The functions return an array of team objects in the resolved order (top seed first).
 */

export const resolveBig12Ties = (tiedTeams, confTeams, games, options = {}) => {
  // wrapper with validation
  if (!Array.isArray(tiedTeams) || tiedTeams.length === 0) return [];
  if (tiedTeams.length === 1) return tiedTeams;
  return resolveBig12Tie(tiedTeams, confTeams, games, options);
};

const resolveBig12Tie = (tiedTeams, confTeams, games, options = {}) => {


  const { ssarankings = null, rng = Math.random } = options;

  if(tiedTeams.length === confTeams.length) return tiedTeams;

  // Base: two-team tie uses two-team procedure
  if (tiedTeams.length === 1) return tiedTeams;
  if (tiedTeams.length === 2) {
    return resolveTwoTeamTie(tiedTeams[0], tiedTeams[1], confTeams, games, { ssarankings, rng });
  }

  // Multi-team tie (3+)
  // Work on a copy
  let remaining = [...tiedTeams];

  // Step a: winning pct in games among tied teams
  const h2hMap = remaining.map(t => {
    const headGames = games.filter(
      g =>
        (g.homeTeam === t.team || g.awayTeam === t.team) &&
        remaining.some(r => r.team !== t.team && (g.homeTeam === r.team || g.awayTeam === r.team))
    );
    const wins = headGames.filter(g => g.winner === t.team).length;
    const total = headGames.length;
    const pct = total > 0 ? wins / total : null; // null => they did not play others in tie
    return { teamObj: t, pct, wins, total };
  });

  // If at least one team has null pct (didn't play everyone), check special subcase:
  //  a. If one team defeated every other team in the tied group (i.e., beat all other tied teams it played), remove that team (seed it),
  //     then restart the multi-team process with the remaining teams (per rule).
  // We'll specifically check "defeated all other teams involved in the tie" � meaning it has head-to-head wins vs every other tied team that it played OR beat each (if they didn't all play each other).
  // Implementation: if a team has direct wins over all *other* tied teams (i.e., has a match and won vs each other team), seed that team.
  const defeatedAll = remaining.filter(t => {
    // For each other tied team, check if there's a game with a winner === t.team and opponent === other.team
    return remaining
      .filter(o => o.team !== t.team)
      .every(other =>
        games.some(
          g =>
            ((g.homeTeam === t.team && g.awayTeam === other.team) ||
              (g.awayTeam === t.team && g.homeTeam === other.team)) &&
            g.winner === t.team
        )
      );
  });

  if (defeatedAll.length === 1) {
    // seed that team first, then recursively handle remaining
    const top = defeatedAll[0];
    const rest = remaining.filter(r => r.team !== top.team);
    return [top, ...resolveBig12Tie(rest, confTeams, games, options)];
  }

  // Next, if head-to-head among tied teams produced clear ordering (i.e., all had non-null pct and there's a top pct > others),
  const allPlayedEachOther = h2hMap.every(m => m.pct !== null);
  if (allPlayedEachOther) {
    // Sort descending by pct. If tie reduces to two, fall back to two-team procedure for that pair.
    h2hMap.sort((a, b) => b.pct - a.pct || b.wins - a.wins);
    // If top has unique pct > next, seed it and recurse
    if (h2hMap[0].pct > h2hMap[1].pct) {
      const top = h2hMap[0].teamObj;
      const rest = remaining.filter(r => r.team !== top.team);
      return [top, ...resolveBig12Tie(rest, confTeams, games, options)];
    }
    // else continue to next multi-team criterion
  }

  // Step b: win pct vs all common conference opponents played by all other tied teams
  const coResults = remaining.map(t => ({
    teamObj: t,
    pct: winPctVsCommonOpponentsMulti(t.team, remaining, games)
  }));
  coResults.sort((a, b) => b.pct - a.pct);

  if (coResults[0].pct > coResults[1].pct) {
    const top = coResults[0].teamObj;
    const rest = remaining.filter(r => r.team !== top.team);
    return [top, ...resolveBig12Tie(rest, confTeams, games, options)];
  }

  // Step c: Record vs next highest placed common opponent in standings (proceeding through standings).
  // We must treat groups of tied teams in confStandings as a collective � use their collective win pct vs the group.
  const coboResults = remaining.map(t => ({
    teamObj: t,
    pct: winPctVsNextHighestCommonOppByOrderMulti(t.team, remaining, games, confTeams)
  }));
  coboResults.sort((a, b) => b.pct - a.pct);

  if (coboResults[0].pct > coboResults[1].pct) {
    const top = coboResults[0].teamObj;
    const rest = remaining.filter(r => r.team !== top.team);
    return [top, ...resolveBig12Tie(rest, confTeams, games, options)];
  }

  // Step d: combined win pct in conference games of conference opponents (strength of conference schedule)
  const combinedResults = remaining.map(t => ({
    teamObj: t,
    pct: combinedOppWinPct(t.team, games, confTeams)
  }));
  combinedResults.sort((a, b) => b.pct - a.pct);
  if (combinedResults[0].pct > combinedResults[1].pct) {
    const top = combinedResults[0].teamObj;
    const rest = remaining.filter(r => r.team !== top.team);
    return [top, ...resolveBig12Tie(rest, confTeams, games, options)];
  }

  // Step e: total number of wins in a 12-game season (apply FCS counting rule and exclude exempt games)
  // We will use confTeams totalWins if provided; otherwise compute from games.
  const totalWinsResults = remaining.map(t => ({
    teamObj: t,
    wins: totalWins12GameSeason(t.team, games, confTeams)
  }));
  totalWinsResults.sort((a, b) => b.wins - a.wins);
  if (totalWinsResults[0].wins > totalWinsResults[1].wins) {
    const top = totalWinsResults[0].teamObj;
    const rest = remaining.filter(r => r.team !== top.team);
    return [top, ...resolveBig12Tie(rest, confTeams, games, options)];
  }

  // Step f: SportSource Analytics rating (highest ranking by S S A)
  if (ssarankings) {
    const ssar = remaining.map(t => ({
      teamObj: t,
      score: ssarankings[t.team] ?? -Infinity
    }));
    ssar.sort((a, b) => b.score - a.score);
    if (ssar[0].score > ssar[1].score) {
      const top = ssar[0].teamObj;
      const rest = remaining.filter(r => r.team !== top.team);
      return [top, ...resolveBig12Tie(rest, confTeams, games, options)];
    }
  }

  // Step g: coin toss (final)
  // We'll shuffle remaining deterministically if rng provided; otherwise Math.random
  const orderedByCoin = [...remaining].sort((a, b) => {
    // compare random numbers
    const ra = rng();
    const rb = rng();
    return ra - rb;
  });

  // Return coin-ordered list
  return orderedByCoin;
};

/* ---------- Two-team tiebreaker ---------- */
const resolveTwoTeamTie = (A, B, confTeams, games, options = {}) => {
  const { ssarankings = null, rng = Math.random } = options;

  // a) Head-to-head
  const h2h = headToHead(A.team, B.team, games);
  if (h2h) return h2h.map(t => ({ team: t })); // return array of objects consistent with tiedTeams shape

  // b) Win % vs all common conference opponents
  const co = twoTeamCommonOppPct(A.team, B.team, games);
  if (co !== null) {
    return co > 0 ? [A, B] : [B, A];
  }

  // c) Win % vs next highest-placed common opponent in standings (proceeding through standings).
  const cobo = twoTeamVsNextHighestByOrder(A.team, B.team, games, confTeams);
  if (cobo !== null) {
    return cobo > 0 ? [A, B] : [B, A];
  }

  // d) Combined win % in conference games of conference opponents (strength of schedule)
  const aCombined = combinedOppWinPct(A.team, games, confTeams);
  const bCombined = combinedOppWinPct(B.team, games, confTeams);
  if (aCombined !== bCombined) {
    return aCombined > bCombined ? [A, B] : [B, A];
  }

  // e) Total number of wins in a 12-game season
  const aWins = totalWins12GameSeason(A.team, games, confTeams);
  const bWins = totalWins12GameSeason(B.team, games, confTeams);
  if (aWins !== bWins) {
    return aWins > bWins ? [A, B] : [B, A];
  }

  // f) SportSource Analytics
  if (ssarankings) {
    const aScore = ssarankings[A.team] ?? -Infinity;
    const bScore = ssarankings[B.team] ?? -Infinity;
    if (aScore !== bScore) return aScore > bScore ? [A, B] : [B, A];
  }

  // g) coin toss
  return rng() < 0.5 ? [A, B] : [B, A];
};

/* ---------- Helper functions ---------- */

const headToHead = (teamA, teamB, games) => {
  // find head-to-head game(s). If multiple, use aggregate win count; if one, winner decides.
  const matches = games.filter(
    g =>
      (g.homeTeam === teamA && g.awayTeam === teamB) ||
      (g.homeTeam === teamB && g.awayTeam === teamA)
  );

  if (!matches.length) return null;

  const aWins = matches.filter(m => m.winner === teamA).length;
  const bWins = matches.filter(m => m.winner === teamB).length;

  if (aWins > bWins) return [teamA, teamB];
  if (bWins > aWins) return [teamB, teamA];
  return null; // still tied
};

const mapTeamGames = (team, games) =>
  games
    .filter(g => g.homeTeam === team || g.awayTeam === team)
    .map(g => ({
      opponent: g.homeTeam === team ? g.awayTeam : g.homeTeam,
      won: g.winner === team,
      conferenceGame: !!g.conferenceGame,
      exempt: !!g.exempt
    }));

// Two-team: win pct vs all common conference opponents
const twoTeamCommonOppPct = (Ateam, Bteam, games) => {
  const Amap = mapTeamGames(Ateam, games).filter(g => g.conferenceGame);
  const Bmap = mapTeamGames(Bteam, games).filter(g => g.conferenceGame);

  const Aopps = new Set(Amap.map(g => g.opponent));
  const Bopps = new Set(Bmap.map(g => g.opponent));
  const common = [...Aopps].filter(o => Bopps.has(o));

  if (common.length === 0) return null; // cannot compute

  const Agames = Amap.filter(g => common.includes(g.opponent));
  const Bgames = Bmap.filter(g => common.includes(g.opponent));

  const Awins = Agames.filter(g => g.won).length;
  const Bwins = Bgames.filter(g => g.won).length;

  const Apct = Agames.length > 0 ? Awins / Agames.length : 0;
  const Bpct = Bgames.length > 0 ? Bwins / Bgames.length : 0;

  if (Apct === Bpct) return null;
  return Apct > Bpct ? 1 : -1;
};

// Multi-team: win pct vs common opponents played by all other tied teams
const winPctVsCommonOpponentsMulti = (team, tiedTeams, games) => {
  // get opponents list for each tied team (conference opponents only)
  const opponentsByTeam = tiedTeams.map(t => ({
    team: t.team,
    opponents: mapTeamGames(t.team, games).filter(g => g.conferenceGame).map(g => g.opponent)
  }));

  // intersection
  const commonOpps = opponentsByTeam
    .map(t => t.opponents)
    .reduce((acc, opps) => acc.filter(o => opps.includes(o)), opponentsByTeam[0].opponents || []);

  if (!commonOpps || commonOpps.length === 0) return 0;

  const teamGames = mapTeamGames(team, games).filter(g => g.conferenceGame && commonOpps.includes(g.opponent));
  if (!teamGames.length) return 0;

  const wins = teamGames.filter(g => g.won).length;
  return wins / teamGames.length;
};

// Multi-team: win pct vs next highest placed common opponent by order (standings)
const winPctVsNextHighestCommonOppByOrderMulti = (team, tiedTeams, games, confTeams) => {
  // Build common opponents played by all tied teams (conference only)
  const opponentsByTeam = tiedTeams.map(t => ({
    team: t.team,
    opponents: mapTeamGames(t.team, games).filter(g => g.conferenceGame).map(g => g.opponent)
  }));

  const commonOpps = opponentsByTeam
    .map(t => t.opponents)
    .reduce((acc, opps) => acc.filter(o => opps.includes(o)), opponentsByTeam[0].opponents || []);

  if (!commonOpps || commonOpps.length === 0) return 0;

  // Sort confTeams by conference record (highest first). Expect confTeams entries to have conferenceWins and conferenceGames (optional)
  const getConfPct = c => {
    if (typeof c.conferenceWins === 'number' && typeof c.conferenceGames === 'number' && c.conferenceGames > 0) {
      return c.conferenceWins / c.conferenceGames;
    }
    // fallback: assume 9-game conference schedule
    if (typeof c.conferenceWins === 'number') return c.conferenceWins / 9;
    return 0;
  };
  const commonSorted = confTeams
    .filter(ct => commonOpps.includes(ct.team))
    .slice() // copy
    .sort((a, b) => getConfPct(b) - getConfPct(a));

  // For the first common opponent in descending conference finish that all tied teams played against,
  // compare win percentage of the tied teams vs that opponent (if multiple games vs that opponent, count all games).
  for (const opp of commonSorted) {
    const teamGames = mapTeamGames(team, games).filter(g => g.conferenceGame && g.opponent === opp.team);
    if (teamGames.length === 0) continue;
    const wins = teamGames.filter(g => g.won).length;
    const pct = wins / teamGames.length;
    // Now compute average/team pct among other tied teams vs this opp to decide ordering.
    // But the rule says compare records of the tied teams vs that opponent; so we will return that pct (higher is better).
    return pct;
  }

  return 0;
};

// Two-team: compare vs next highest placed common opponent in standings
const twoTeamVsNextHighestByOrder = (Ateam, Bteam, games, confTeams) => {
  // Build set of opponents for each (conference games only)
  const Aopps = mapTeamGames(Ateam, games).filter(g => g.conferenceGame).map(g => g.opponent);
  const Bopps = mapTeamGames(Bteam, games).filter(g => g.conferenceGame).map(g => g.opponent);
  const common = Aopps.filter(o => Bopps.includes(o));

  if (!common.length) return null;

  // sort confTeams by conference record (use conferenceWins/conferenceGames if present)
  const getConfPct = c => {
    if (typeof c.conferenceWins === 'number' && typeof c.conferenceGames === 'number' && c.conferenceGames > 0) {
      return c.conferenceWins / c.conferenceGames;
    }
    if (typeof c.conferenceWins === 'number') return c.conferenceWins / 9;
    return 0;
  };

  const ordered = confTeams.slice().sort((a, b) => getConfPct(b) - getConfPct(a));

  for (const opp of ordered) {
    if (!common.includes(opp.team)) continue;
    // collect all games each team played vs opp (conference games only)
    const Agames = mapTeamGames(Ateam, games).filter(g => g.conferenceGame && g.opponent === opp.team);
    const Bgames = mapTeamGames(Bteam, games).filter(g => g.conferenceGame && g.opponent === opp.team);

    if (!Agames.length && !Bgames.length) continue;

    const Apct = Agames.length ? Agames.filter(g => g.won).length / Agames.length : 0;
    const Bpct = Bgames.length ? Bgames.filter(g => g.won).length / Bgames.length : 0;

    if (Apct === Bpct) continue;
    return Apct > Bpct ? 1 : -1;
  }

  return null;
};

// Combined win pct of conference opponents (strength of conference schedule)
const combinedOppWinPct = (team, games, confTeams) => {
  // 1) get list of conference opponents played (conferenceGame === true)
  const opps = games
    .filter(g => (g.homeTeam === team || g.awayTeam === team) && g.conferenceGame === true)
    .map(g => (g.homeTeam === team ? g.awayTeam : g.homeTeam));

  if (!opps.length) return 0;

  const oppPcts = opps.map(op => {
    const rec = confTeams.find(c => c.team === op);
    if (!rec) return 0;
    if (typeof rec.conferenceWins === 'number' && typeof rec.conferenceGames === 'number' && rec.conferenceGames > 0)
      return rec.conferenceWins / rec.conferenceGames;
    if (typeof rec.conferenceWins === 'number') return rec.conferenceWins / 9;
    return 0;
  });

  const total = oppPcts.reduce((s, v) => s + v, 0);
  return total / oppPcts.length;
};

// Total wins in a 12-game season with the FCS rule and exempt game exclusion.
// We'll use confTeams.totalWins if provided (prefer that because it's authoritative).
const totalWins12GameSeason = (team, games, confTeams) => {
  const rec = confTeams.find(c => c.team === team);
  if (rec && typeof rec.totalWins === 'number') return rec.totalWins;

  // Otherwise compute from games: count wins, but only count max 1 win vs FCS/lower (we need an indicator; assume game has property 'opponentDivision' maybe)
  // Since we don't have opponent division info in many setups, we'll implement a conservative approach:
  // - Count all wins from games that are not marked exempt.
  // - If a game has g.opponentDivision === 'FCS' or g.isFCS === true, count at most one such win.
  const teamGames = games.filter(g => g.homeTeam === team || g.awayTeam === team);
  let wins = 0;
  let fcsWins = 0;
  for (const g of teamGames) {
    if (g.exempt) continue; // exclude exempt games
    if (g.winner === team) {
      if (g.opponentDivision === 'FCS' || g.isFCS === true) {
        fcsWins++;
        if (fcsWins <= 1) wins++;
      } else wins++;
    }
  }
  return wins;
};

